import { Box, Chip, CircularProgress, Collapse, Stack, Typography } from "@mui/material";
import { forwardRef, lazy, Suspense } from "react";
import { GlossaryGuide } from "../../../models/Glossary";

// Loaded when a guide is first opened: react-markdown is ESM that Jest can't parse, and every test that renders
// the query builder imports the glossary; it also keeps the markdown stack out of the query builder's bundle.
const GlossaryGuideBody = lazy(() => import("./GlossaryGuideBody"));

const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

interface Props {
  guide: GlossaryGuide;
  expanded: boolean;
  onToggle: () => void;
  /** Finds a covered metric in the list below (sets the search to its name). */
  onShowMetric: (name: string) => void;
}

/** One guide: a title row like an entry's, expanding to the markdown body (GlossaryGuideBody). */
const GlossaryGuideRow = forwardRef<HTMLDivElement, Props>(function GlossaryGuideRow(
  { guide, expanded, onToggle, onShowMetric },
  ref,
) {
  return (
    <Box
      ref={ref}
      sx={{
        scrollMarginTop: "32px",
        borderLeft: "3px solid",
        borderLeftColor: expanded ? "#4c4c4c" : "transparent",
        borderBottom: "1px solid",
        borderBottomColor: expanded ? "#d0d0d0" : "#ededed",
        bgcolor: "#fff",
        boxShadow: expanded ? "0 1px 3px rgba(0,0,0,0.10)" : "none",
        position: expanded ? "relative" : "static",
        zIndex: expanded ? 1 : "auto",
      }}
    >
      <Box
        onClick={onToggle}
        sx={{ px: 1.25, py: 0.75, cursor: "pointer", "&:hover": { bgcolor: expanded ? "#fff" : "#f7f7f7" } }}
      >
        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>{guide.title}</Typography>
        {guide.summary && (
          <Typography sx={{ fontSize: "0.75rem", color: "rgba(0,0,0,0.6)" }}>
            {guide.summary}
          </Typography>
        )}
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box
          sx={{
            px: 1.5,
            pb: 1.5,
            borderTop: "1px dashed",
            borderTopColor: "#e4e4e4",
          }}
        >
          <Suspense fallback={<CircularProgress size={16} sx={{ my: 1 }} />}>
            <GlossaryGuideBody markdown={guide.body} />
          </Suspense>

          {guide.metrics.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mt: 1.5 }}>
              <Typography sx={{ fontSize: "0.68rem", color: "rgba(0,0,0,0.5)", mr: 0.5 }}>
                Entries:
              </Typography>
              {guide.metrics.map((name) => (
                <Chip
                  key={name}
                  label={name}
                  size="small"
                  onClick={() => onShowMetric(name)}
                  sx={{
                    height: 20,
                    borderRadius: "3px",
                    fontSize: "0.7rem",
                    fontFamily: MONO,
                    bgcolor: "#eceff1",
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Collapse>
    </Box>
  );
});

export default GlossaryGuideRow;
